import {makeAutoObservable} from "mobx";
import type {AdvancedFilter, CategoryFilter, HttpQueryParams} from "~/services/ApiTypes";
import {
    countActiveFilters,
    encodeFilterForUrl,
    sanitizeFilter,
    selectedCategoryPath,
    toQueryParams,
} from "./spendLandscapeFilters";
import {LOCAL_STORAGE_KEY_FILTERS} from "./spendLandscapeFilterStorage";

/**
 * Which data to reload after the applied filters changed. Empty means everything.
 */
export type ReloadScope = {
    except?: ('tree' | 'geo')[]
    only?: Record<string, boolean>
    /** Replaces `only` while the synergy view is split by source */
    onlyWhenSplit?: Record<string, boolean>
}

/**
 * Filter state of the Spend Landscape page.
 *
 * - `filters` are applied: every chart and table is loaded with them.
 * - `draft` holds the changes made in the filter bar that are not applied yet (the Apply button).
 * - Clicks on charts and table rows apply straight away, together with any draft.
 */
export class SpendLandscapeFilterStore {
    filters: AdvancedFilter = {}
    draft: AdvancedFilter | null = null
    /** Initial filter based on the customer and databag configuration */
    initialFilter: AdvancedFilter | null = null
    /** The filter that was read from Local Storage */
    localFilter: AdvancedFilter | null = null
    /** A filter from a shared link, waiting for the configuration to load */
    private pendingUrlFilter: AdvancedFilter | null = null

    constructor(private readonly onApplied: (scope: ReloadScope) => void) {
        makeAutoObservable<SpendLandscapeFilterStore, 'onApplied'>(this, {onApplied: false});
    }

    get loaded(): boolean {
        return this.initialFilter !== null && this.localFilter !== null;
    }

    /** The applied filters with the draft on top: what the filter bar shows and searches its options with. */
    get editingFilters(): AdvancedFilter {
        return this.draft ? {...this.filters, ...this.draft} : this.filters;
    }

    get hasStagedChanges(): boolean {
        if (!this.draft) return false;
        return Object.keys(this.draft).some(key =>
            JSON.stringify(this.draft?.[key]) !== JSON.stringify(this.filters[key]));
    }

    /** Number of filters set on top of the defaults */
    get activeFilterCount(): number {
        return countActiveFilters(this.filters, this.initialFilter ?? {});
    }

    get clearDisabled(): boolean {
        return this.activeFilterCount === 0 && !this.hasStagedChanges;
    }

    get singleSelectedCategory(): CategoryFilter | null {
        return selectedCategoryPath(this.filters);
    }

    /** The filters for a shareable link, or null when they are the defaults */
    get urlValue(): string | null {
        if (!this.loaded) return null;
        const value = encodeFilterForUrl(this.filters);
        return value === encodeFilterForUrl(this.initialFilter ?? {}) ? null : value;
    }

    queryParams(...exclude: string[]): HttpQueryParams {
        return toQueryParams(this.filters, ...exclude);
    }

    editingQueryParams(...exclude: string[]): HttpQueryParams {
        return toQueryParams(this.editingFilters, ...exclude);
    }

    /** Change the filter bar without loading data yet */
    stage(patch: AdvancedFilter) {
        this.draft = {...(this.draft ?? {}), ...patch};
    }

    applyStaged() {
        if (!this.draft) return;
        this.apply({});
    }

    discardStaged() {
        this.draft = null;
    }

    /** Apply a change (and any draft) and reload the data */
    apply(patch: AdvancedFilter, scope: ReloadScope = {}) {
        const hadDraft = this.hasStagedChanges;
        this.filters = {...this.filters, ...(this.draft ?? {}), ...patch};
        this.draft = null;
        this.saveLocally();
        this.onApplied(hadDraft ? {} : scope);
    }

    /** Back to the defaults of the customer and databag */
    reset() {
        if (!this.loaded) {
            console.warn('SpendLandscapeFilterStore.reset(): filters are not loaded yet...');
            return;
        }
        localStorage.removeItem(LOCAL_STORAGE_KEY_FILTERS);
        this.filters = {...this.initialFilter};
        this.draft = null;
        this.onApplied({});
    }

    setInitialFilter(initialFilter: AdvancedFilter) {
        this.initialFilter = initialFilter;
        this.checkLoaded();
    }

    /** Apply the filters of a shared link; they replace the remembered filters */
    setUrlFilter(urlFilter: AdvancedFilter) {
        if (!this.loaded) {
            this.pendingUrlFilter = urlFilter;
            return;
        }
        this.filters = {...this.initialFilter, ...urlFilter};
        this.draft = null;
        this.saveLocally();
        this.onApplied({});
    }

    loadLocalFilter() {
        let localFilter: AdvancedFilter = {};
        const localFilterString = localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS);
        if (localFilterString) {
            try {
                localFilter = sanitizeFilter(JSON.parse(localFilterString));
            } catch (e) {
                console.error(e);
            }
        }
        this.localFilter = localFilter;
        this.checkLoaded();
    }

    private saveLocally() {
        if (this.loaded) {
            localStorage.setItem(LOCAL_STORAGE_KEY_FILTERS, JSON.stringify(this.filters));
        }
    }

    private checkLoaded() {
        if (!this.loaded) return;
        this.filters = {...this.filters, ...this.initialFilter, ...this.localFilter, ...(this.pendingUrlFilter ?? {})};
        this.pendingUrlFilter = null;
        this.draft = null;
        this.onApplied({});
    }
}
